import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import {
  PhoneMissed,
  MessageSquareText,
  BrainCircuit,
  Headset,
} from "lucide-react";

const steps = [
  {
    icon: PhoneMissed,
    time: "0s",
    title: "Missed call detected",
    body: "A homeowner rings while your team is tied up. Backfill catches it instantly — no voicemail black hole.",
  },
  {
    icon: MessageSquareText,
    time: "8s",
    title: "Instant branded SMS",
    body: "The caller gets a fast, company-branded text from your business — not a generic bot number.",
  },
  {
    icon: BrainCircuit,
    time: "45s",
    title: "Smart qualification",
    body: "Repair, maintenance, emergency, or replacement quote? Backfill asks the basics and captures address, system age, and urgency.",
  },
  {
    icon: Headset,
    time: "2m",
    title: "Human escalation & booking",
    body: "Urgent, confusing, or sensitive conversations route to a real dispatcher or your on-call tech. Your office books the slot.",
  },
];

const DEFAULT_CHAT = [
  { from: "backfill", text: "Hi, this is Apex Heating & Air — sorry we missed your call. How can we help?" },
  { from: "customer", text: "AC stopped cooling and it's 95° inside!" },
  { from: "backfill", text: "Sorry about that — sounds like an emergency repair. Is anyone home right now?" },
  { from: "customer", text: "Yes, we're home all day." },
  { from: "backfill", text: "A dispatcher has been looped in. Earliest tech window: today, 2–4 PM. Confirm?" },
];

export default function HowItWorks({ chat = DEFAULT_CHAT }) {
  return (
    <section id="how" data-testid="how-it-works-section" className="relative border-t border-white/[0.06] bg-[#0E1117] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <Reveal>
              <Eyebrow>How It Works</Eyebrow>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
                From ring-out to booked job in under two minutes
              </h2>
            </Reveal>
            <div className="mt-10 space-y-4">
              {steps.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    data-testid={`workflow-step-${i}`}
                    className="group flex gap-5 rounded-lg border border-white/[0.08] bg-[#12161F] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#F25F22]/40"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-[#F25F22]/30 bg-[#F25F22]/10">
                      <s.icon className="h-5 w-5 text-[#F25F22]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-400">
                          {s.time}
                        </span>
                        <h3 className="font-display text-base font-semibold text-slate-50">
                          {s.title}
                        </h3>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                        {s.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="relative mx-auto w-full max-w-[360px]" data-testid="phone-simulator">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-[#F25F22]/[0.07] blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-white/[0.12] bg-[#0B0D10] shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/[0.08] bg-[#12161F] px-5 py-3.5">
                  <div>
                    <p className="text-sm font-semibold text-slate-100">
                      Apex Heating &amp; Air
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                      Backfill active
                    </p>
                  </div>
                  <span className="relative flex h-2 w-2">
                    <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                </div>
                <div className="space-y-3 p-5">
                  {chat.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: 0.3 + i * 0.35 }}
                      className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                          m.from === "customer"
                            ? "rounded-br-sm bg-[#F25F22] text-white"
                            : "rounded-bl-sm border border-white/[0.08] bg-[#1A212E] text-slate-200"
                        }`}
                      >
                        {m.text}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
